import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface InlineSelectOptionProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  /** Unique value for this option */
  value: string
  /** Whether this option is currently selected */
  selected?: boolean
  /** Whether this option is disabled */
  disabled?: boolean
  /** Called when this option is clicked */
  onSelect?: (value: string) => void
  /** Display text for the option */
  children: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled InlineSelectOption component.
 *
 * Represents a single option within an InlineSelect dropdown.
 * Compact styling suitable for inline dropdowns.
 * All visual state determined by props. Spreads ...props for HOC injection.
 */
const InlineSelectOption = React.memo(
  ({
    value,
    selected = false,
    disabled = false,
    onSelect,
    children,
    style,
    onClick,
    onKeyDown,
    className,
    ...props
  }: InlineSelectOptionProps) => {
    const [css, theme] = useStyletron()

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return
      onSelect?.(value)
      onClick?.(e)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onSelect?.(value)
      }
      onKeyDown?.(e)
    }

    const optionClassName = css(getOptionStyles(theme, { selected, disabled }))

    return (
      <div
        role="option"
        aria-selected={selected}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        data-value={value}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`${optionClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {children}
      </div>
    )
  }
)

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

interface StyleParams {
  selected: boolean
  disabled: boolean
}

const getOptionStyles = (theme: any, { selected, disabled }: StyleParams) => ({
  padding: '8px 12px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '13px',
  lineHeight: '18px',
  color: disabled ? theme.colors.grey50 : theme.colors.grey70,
  backgroundColor:
    selected && !disabled ? theme.colors.primary50 : 'transparent',
  fontWeight: selected && !disabled ? 500 : 'normal',
  transition: 'background-color 150ms ease',
  whiteSpace: 'nowrap' as const,
})

export default InlineSelectOption
